import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { Policy } from '../../../../../shared/models/info/policy';
import { NoteIconComponent } from '../../../icons/note-icon/note-icon.component';
import { CommonUtils } from '../../../../common/common-utils';
import { CollapsableNodeStateStoreService } from '../../../../services/collapsable-node-state-store.service';
import { ChevronDoubleDownComponent } from '../../../icons/chevron-double-down-icon/chevron-double-down-icon.component';
import { ChevronDoubleUpIconComponent } from '../../../icons/chevron-double-up-icon/chevron-double-up-icon.component';
import { ChevronIconComponent } from '../../../icons/table/chevron-icon/chevron-icon.component';
import { ARROWS } from '../../../../../shared/constants/arrows';
import { GroupedNwNumber } from '../../../../../shared/models/nw-number/grouped-nw-number';
import { Project } from '../../../../../shared/models/project';
import { NwNumberNumberComponent } from '../../../common/inputs/nw-number-number/nw-number-number.component';
import { CheckComponent } from '../../../icons/check/check.component';
import { AddComponent } from '../../../icons/add/add.component';
import { IfPolicySatisfiedDirective } from '../../../../directives/if-policy-satisfied.directive';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { CreateNwNumber } from '../../../../../shared/models/nw-number/create-nw-number';
import { ImportNwNumberPopover } from '../../../nw-number/popover/import-nw-number-popover/import-nw-number-popover';
import { NwNumber } from '../../../../../shared/models/nw-number/nw-number';
import { ImportType } from './import-type';
import { UploadFileComponent } from '../../../common/inputs/upload-file/upload-file.component';
import { ImportProjectPopover } from '../../../nw-number/popover/import-project-popover/import-project-popover';
import { ExportNwNumberPopover } from '../../../nw-number/popover/export-nw-number-popover/export-nw-number-popover';
import { ExportType } from './export-type';

@Component({
  selector: 'app-nw-number-accordion',
  imports: [
    NgClass,
    DatePipe,
    NoteIconComponent,
    ChevronDoubleDownComponent,
    ChevronDoubleUpIconComponent,
    ChevronIconComponent,
    NwNumberNumberComponent,
    CheckComponent,
    AddComponent,
    IfPolicySatisfiedDirective,
    AgentActionDirective,
    ImportNwNumberPopover,
    UploadFileComponent,
    ImportProjectPopover,
    ExportNwNumberPopover,
  ],
  templateUrl: './nw-number-accordion.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './nw-number-accordion.component.scss',
})
export class NwNumberAccordionComponent implements OnChanges {
  @Input() public groupedNwNumbers?: GroupedNwNumber[];
  @Input() public selectedNwNumberId?: string;
  /**
   * Emitted when a nw number is clicked.
   * The emitted value is the ID of nw number.
   */
  @Output() public nwNumberClicked = new EventEmitter<string>();
  /**
   * Emitted when the sidebar for a specific project should be opened.
   * The emitted value is the ID of the project
   */
  @Output() public projectClicked = new EventEmitter<string>();
  @Output() public createNwNumberClicked = new EventEmitter<CreateNwNumber>();
  @Output() public nwNumberImportClicked = new EventEmitter<{
    nwNumber: NwNumber;
    files: File[];
    importType: ImportType;
  }>();
  @Output() public projectImportClicked = new EventEmitter<{
    project: NwNumber;
    files: File[];
    importType: ImportType;
  }>();
  @Output() public nwNumberExportClicked = new EventEmitter<{
    nwNumber: NwNumber;
    exportType: ExportType;
  }>();
  @Output() public publishEventsClicked = new EventEmitter<string>();
  public readonly activeElements = new Map<string, boolean>();
  public readonly policy = Policy;
  public readonly commonUtils = CommonUtils;
  public arrows = ARROWS;
  public projectIdForCreateNwNumber?: Project;

  public importFiles?: File[];
  public nwNumberForUpload?: NwNumber;
  public projectForUpload?: Project;
  public allowMultiFileImport = false;
  public acceptedFileType = '.xml';
  private importType = ImportType.ImportErrorCodes;

  private readonly collapsableStateStore = inject(CollapsableNodeStateStoreService);
  private settingsKeyId = 'NwNumberAccordion';
  private expandableElementsRegex = /(?!NW\d{3}(-[A-Z]{1-2})?)/;
  private nwNumberToCreate?: string;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['groupedNwNumbers']) {
      this.initActiveElements();
    }
  }

  public isActive(element: string): boolean {
    return this.activeElements.get(element) || false;
  }

  public isSelected(nwNumberId: string | undefined): boolean {
    return this.selectedNwNumberId === nwNumberId;
  }

  public toggleActive(element: string, hasSubItems: boolean): void {
    if (!hasSubItems) {
      return;
    }
    const currentState = this.activeElements.get(element) || false;
    this.setElementFoldingState(element, !currentState);
  }

  public expandAll(): void {
    this.setAllElementFoldingStates(true);
  }

  public collapseAll(): void {
    this.setAllElementFoldingStates(false);
  }

  public onNwNumberClicked(nwNumberId: string): void {
    this.nwNumberClicked.emit(nwNumberId);
  }

  public openProjectSidebar(projectId: string): void {
    this.selectedNwNumberId = projectId;
    this.projectClicked.emit(projectId);
  }

  public nwNumberNumberEntered(nwNumber: string): void {
    this.nwNumberToCreate = nwNumber;
  }

  public triggerCreateNwNumber(): void {
    const createNumberData: CreateNwNumber = {
      projectId: this.projectIdForCreateNwNumber!.id!,
      number: this.nwNumberToCreate!,
    };
    this.createNwNumberClicked.emit(createNumberData);
    this.projectIdForCreateNwNumber = undefined;
    this.nwNumberToCreate = undefined;
  }

  public toggleEnterNwNumber(project: Project): void {
    this.projectIdForCreateNwNumber = this.projectIdForCreateNwNumber?.id === project.id ? undefined : project;
  }

  public setProjectUpload(project: Project, importType: ImportType): void {
    this.nwNumberForUpload = undefined;
    this.projectForUpload = this.projectForUpload?.id === project.id ? undefined : project;
    this.importType = importType;
    this.acceptedFileType = '.xml';
    this.allowMultiFileImport = importType == ImportType.ImportAlarmMaskTranslation;
  }

  public setNwNumberUpload(nwNumber: NwNumber, importType: ImportType): void {
    this.projectForUpload = undefined;
    this.nwNumberForUpload = this.nwNumberForUpload?.id === nwNumber.id ? undefined : nwNumber;
    this.importType = importType;
    this.acceptedFileType = importType == ImportType.ImportErrorCodes ? '.xlsx' : '.xml,.xlsx';
    this.allowMultiFileImport = importType == ImportType.ImportAlarmMaskTranslation;
  }

  public emitNwNumberExport(nwNumber: NwNumber, exportType: ExportType): void {
    this.nwNumberExportClicked.emit({
      nwNumber: nwNumber,
      exportType: exportType,
    });
  }

  public emitPublishEvents(nwNumber: NwNumber): void {
    this.publishEventsClicked.emit(nwNumber.id);
  }

  public onFilesSelected(files: File[]): void {
    this.importFiles = files;
  }

  public uploadNwNumberFile(): void {
    this.nwNumberImportClicked.emit({
      nwNumber: this.nwNumberForUpload!,
      files: this.importFiles!,
      importType: this.importType,
    });
    this.nwNumberForUpload = undefined;
    this.importFiles = undefined;
  }

  public uploadProjectFile(): void {
    this.projectImportClicked.emit({
      project: this.projectForUpload!,
      files: this.importFiles!,
      importType: this.importType,
    });
    this.projectForUpload = undefined;
    this.importFiles = undefined;
  }

  private setElementFoldingState(element: string, expanded: boolean): void {
    const projectElement = (this.groupedNwNumbers ?? []).find(
      (groupedNwNumber) => groupedNwNumber.project?.id == element
    );
    if (projectElement && projectElement.nwNumbers && projectElement.nwNumbers.length == 0) {
      return;
    }

    this.activeElements.set(element, expanded);
    this.collapsableStateStore.updateSingleState(this.settingsKeyId, element, expanded);
  }

  private setAllElementFoldingStates(expanded: boolean): void {
    for (const key of this.activeElements.keys()) {
      if (this.expandableElementsRegex.test(key)) {
        this.setElementFoldingState(key, expanded);
      }
    }
  }

  private initActiveElements(): void {
    this.activeElements.clear();

    (this.groupedNwNumbers ?? []).forEach((groupedNwNumber) => {
      const projectId = groupedNwNumber.project?.id ?? '';
      const previouslyActivated = this.collapsableStateStore.getSingleState(this.settingsKeyId, projectId, true);
      this.activeElements.set(projectId, (groupedNwNumber.nwNumbers?.length ?? 0) > 0 && previouslyActivated);
    });
  }
}
